import { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import { ConfirmDialog } from 'src/components/custom-dialog';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { DashboardContent } from 'src/layouts/dashboard';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import { PermissionButton } from 'src/routes/components/permission-button';
import { paths } from 'src/routes/paths';
import ErrorPage403 from 'src/pages/error/403';
import {
  stErrorMessage,
  stReclaimUserRedPacketBalances,
  StRedPacketReclaimResponse,
  StRedPacketReclaimStatus,
} from '../../../services/socialTradingService';
import { parseReclaimEntries } from './parseEntries';
import ReclaimHistory from './ReclaimHistory';

const MAX_ENTRIES = 100;
const MAX_REASON = 500;
const STATUS_COLOR: Record<StRedPacketReclaimStatus, 'success' | 'default' | 'warning' | 'error'> = {
  RECLAIMED: 'success',
  SKIPPED_ZERO: 'default',
  NOT_FOUND: 'warning',
  FAILED: 'error',
};

export default function RedpacketBalanceReclaimPage() {
  const t = useTranslate();
  const notify = useNotify();
  const [text, setText] = useState('');
  const [reason, setReason] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<StRedPacketReclaimResponse | null>(null);
  const [historyKey, setHistoryKey] = useState(0);

  const entries = useMemo(() => parseReclaimEntries(text), [text]);
  const count = entries.userIds.length + entries.walletAddresses.length;
  const reasonTrimmed = reason.trim();
  const canSubmit =
    count > 0 &&
    entries.invalid.length === 0 &&
    count <= MAX_ENTRIES && reasonTrimmed.length > 0 && reasonTrimmed.length <= MAX_REASON;

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await stReclaimUserRedPacketBalances({
        userIds: entries.userIds,
        walletAddresses: entries.walletAddresses,
        reason: reasonTrimmed,
      });
      setResult(res);
      setHistoryKey((k) => k + 1);
      setConfirmOpen(false);
      notify(t('module.socialTrading.reclaimDone', { total: res.totalReclaimed }), { type: 'success' });
    } catch (e) {
      notify(stErrorMessage(e), { type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PermissionGuard resource="st-redpacket-balances" action="update" fallback={<ErrorPage403 />}>
      <DashboardContent maxWidth={false}>
        <CustomBreadcrumbs
          heading={t('module.socialTrading.redpacketBalanceReclaim')}
          links={[
            { name: t('module.socialTrading.title'), href: paths.socialTrading.workbench.root },
            { name: t('module.socialTrading.redpacketBalanceReclaim') },
          ]}
          sx={{ mb: 3 }}
        />
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack spacing={2}>
              <Alert severity="warning">{t('module.socialTrading.reclaimWarning')}</Alert>
              <TextField
                label={t('module.socialTrading.reclaimEntries')}
                helperText={t('module.socialTrading.reclaimEntriesHelp', { count, max: MAX_ENTRIES })}
                error={count > MAX_ENTRIES}
                multiline
                minRows={6}
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
              {entries.invalid.length > 0 && (
                <Alert severity="error">
                  {t('module.socialTrading.reclaimInvalidEntries', {
                    count: entries.invalid.length,
                    list:
                      entries.invalid.slice(0, 10).join(', ') +
                      (entries.invalid.length > 10 ? ` … +${entries.invalid.length - 10}` : ''),
                  })}
                </Alert>
              )}
              <TextField
                label={t('module.socialTrading.reclaimReason')}
                required
                inputProps={{ maxLength: MAX_REASON }}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
              <Stack direction="row">
                <PermissionButton resource="st-redpacket-balances" action="update">
                  <Button variant="contained" color="error" disabled={!canSubmit} onClick={() => setConfirmOpen(true)}>
                    {t('module.socialTrading.reclaimSubmit')}
                  </Button>
                </PermissionButton>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {result && (
          <Card>
            <CardContent>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                {t('module.socialTrading.reclaimTotal', { total: result.totalReclaimed })} · {result.batchId}
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('module.socialTrading.reclaimColInput')}</TableCell>
                    <TableCell>{t('module.socialTrading.reclaimColUserId')}</TableCell>
                    <TableCell>{t('module.socialTrading.reclaimColStatus')}</TableCell>
                    <TableCell align="right">{t('module.socialTrading.reclaimColAmount')}</TableCell>
                    <TableCell align="right">{t('module.socialTrading.reclaimColBalance')}</TableCell>
                    <TableCell align="right">{t('module.socialTrading.reclaimColCancelled')}</TableCell>
                    <TableCell align="right">{t('module.socialTrading.reclaimColProcessing')}</TableCell>
                    <TableCell>{t('module.socialTrading.reclaimColError')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.results.map((r) => {
                    const processing = Number(r.processingWithdrawal) > 0;
                    return (
                      <TableRow key={r.input} sx={processing ? { bgcolor: 'warning.lighter' } : undefined}>
                        <TableCell>{r.input}</TableCell>
                        <TableCell>{r.userId ?? '-'}</TableCell>
                        <TableCell>
                          <Chip size="small" color={STATUS_COLOR[r.status]} label={t(`module.socialTrading.reclaimStatus.${r.status}`)} />
                        </TableCell>
                        <TableCell align="right">{r.amount}</TableCell>
                        <TableCell align="right">{r.balanceAmount}</TableCell>
                        <TableCell align="right">
                          {r.cancelledWithdrawalAmount} ({r.cancelledWithdrawalCount})
                        </TableCell>
                        <TableCell align="right" title={processing ? t('module.socialTrading.reclaimProcessingHint') : undefined}>
                          {r.processingWithdrawal}
                        </TableCell>
                        <TableCell>{r.error ?? ''}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {result.results.some((r) => Number(r.processingWithdrawal) > 0) && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  {t('module.socialTrading.reclaimProcessingHint')}
                </Alert>
              )}
            </CardContent>
          </Card>
        )}

        <ReclaimHistory key={historyKey} />

        <ConfirmDialog
          open={confirmOpen}
          onClose={() => !submitting && setConfirmOpen(false)}
          title={t('module.socialTrading.reclaimConfirmTitle', { count })}
          content={<Typography variant="body2">{t('module.socialTrading.reclaimConfirmBody')}</Typography>}
          action={
            <PermissionButton resource="st-redpacket-balances" action="update">
              <Button variant="contained" color="error" disabled={submitting} onClick={submit}>
                {t('module.socialTrading.reclaimSubmit')}
              </Button>
            </PermissionButton>
          }
        />
      </DashboardContent>
    </PermissionGuard>
  );
}
